import { type QueryClient, queryOptions, useQuery } from "@tanstack/react-query";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { backendService } from "../../../services/backend.service";
import type { WageSettingsResponse } from "../../../types/wageRules";

const WAGE_SETTINGS_QUERY_KEY = "team-wage-settings";

/** Keyed by department, so a cached read for one department can never seed another's tab. */
export const wageSettingsQueryKey = (departmentId: string) => [WAGE_SETTINGS_QUERY_KEY, departmentId] as const;

/**
 * The same read as `useWageSettings`, under the same key (one cache), for a surface that only SHOWS the
 * stored rows elsewhere (the wage breakdown's hover card): no `team_wage_settings_fetch_*` events, which
 * describe the Team tab loading, and no caching policy — the caller spreads in its own.
 */
export const wageSettingsQueryOptions = (departmentId: string) =>
	queryOptions({
		queryKey: wageSettingsQueryKey(departmentId),
		queryFn: async () => (await backendService.getWageSettings(departmentId)).data,
	});

/**
 * DAT-905 · The stored values Team → Wage Settings edits, read for the department in the URL.
 *
 * Not `chosenDepartment`: `/users/user_info` fully loads only the token's department, so a deep link
 * to any other one would show empty rule tables that look saved (and saving one would replace every
 * stored rule) and the default $16 training wage. `data` is `undefined` until THIS department's read
 * lands, which is what every section locks on.
 */
export const useWageSettings = (departmentId: string) => {
	const { track, trackError } = useAnalytics();

	return useQuery<WageSettingsResponse>({
		queryKey: wageSettingsQueryKey(departmentId),
		queryFn: async () => {
			track("team_wage_settings_fetch_start", { department_id: departmentId });
			try {
				const { data } = await backendService.getWageSettings(departmentId);
				track("team_wage_settings_fetch_success", { department_id: departmentId });
				return data;
			} catch (error) {
				trackError("team_wage_settings_fetch_error", { department_id: departmentId, error_message: (error as Error)?.message });
				throw error;
			}
		},
	});
};

/**
 * Writes one saved part into the cached read, so the section's baseline is what the server stored
 * without a refetch. A save is only reachable once the read landed, so there is always an entry; the
 * `undefined` branch is TanStack's own "no entry" contract, not a fallback value.
 */
export const updateWageSettingsCache = (
	queryClient: QueryClient,
	departmentId: string,
	update: (current: WageSettingsResponse) => WageSettingsResponse,
): void => {
	queryClient.setQueryData<WageSettingsResponse>(wageSettingsQueryKey(departmentId), (current) => (current === undefined ? undefined : update(current)));
};
